var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { css } from '@emotion/react';
import { useMemo } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { types, } from './constants';
const borderSize = styleUtils.rem(2);
const iconSizeValues = {
    extraLarge: 44,
    large: 32,
    medium: 24,
};
const baseVariationStyles = {
    destructive: {
        backgroundColor: 'transparent',
        boxShadow: undefined,
        color: theme.colors.negative800,
        disabledBackgroundColor: 'transparent',
        disabledColor: theme.colors.negative800,
        hoverBackgroundColor: undefined,
        hoverTextColor: theme.colors.negative800,
    },
    primary: {
        backgroundColor: theme.colors.gray100,
        boxShadow: `0 0 ${styleUtils.rem(11)} rgba(0, 0, 0, 0.15)`,
        color: theme.colors.gray700,
        disabledBackgroundColor: undefined,
        disabledColor: theme.colors.gray700,
        hoverBackgroundColor: undefined,
        hoverTextColor: theme.colors.gray700,
    },
    primaryAlternate: {
        backgroundColor: theme.colors.accent700,
        boxShadow: `0 0 ${styleUtils.rem(11)} rgba(0, 0, 0, 0.15)`,
        color: theme.colors.gray100,
        disabledBackgroundColor: theme.colors.gray200,
        disabledColor: theme.colors.gray500,
        hoverBackgroundColor: theme.colors.accent800,
        hoverTextColor: undefined,
    },
    secondary: {
        backgroundColor: 'transparent',
        boxShadow: undefined,
        color: 'inherit',
        disabledBackgroundColor: 'transparent',
        disabledColor: 'inherit',
        hoverBackgroundColor: undefined,
        hoverTextColor: 'inherit',
    },
};
const useIconButtonStyles = (_a) => {
    var { modifier, size } = _a, props = __rest(_a, ["modifier", "size"]);
    const variationStyles = useMemo(() => {
        const styleVariation = 'styleVariation' in props ? props.styleVariation : undefined;
        return (modifier === 'custom'
            && styleVariation
            ? Object.assign(Object.assign({}, baseVariationStyles), { custom: styleVariation }) : baseVariationStyles);
    }, [
        modifier,
        props,
    ]);
    const iconButtonStyles = useMemo(() => {
        var _a, _b;
        return (css `
        ${styleUtils.buttonReset}
        ${styleUtils.borderBox}
        ${styleUtils.transition('background-color')}
        ${modifier === types.primary && styleUtils.transition([
            'background-color',
            'color',
            'border',
        ])}
        ${variationStyles[modifier].boxShadow && css `
          box-shadow: ${variationStyles[modifier].boxShadow};
        `}
        align-items: center;
        background-color: ${variationStyles[modifier].backgroundColor};
        border-radius: 50%;
        border: ${borderSize} solid transparent;
        color: ${variationStyles[modifier].color};
        display: inline-flex;
        height: ${styleUtils.rem(iconSizeValues[size])};
        justify-content: center;
        letter-spacing: ${styleUtils.rem(0.5)};
        padding: calc(${theme.spacing.base} - ${borderSize});
        white-space: nowrap;
        width: ${styleUtils.rem(iconSizeValues[size])};

        &:active {
          background-color: ${theme.colors.gray300};
        }

        &:focus,
        &:hover {
          outline: none;
          cursor: pointer;
        }

        &:disabled {
          cursor: default;
          ${variationStyles[modifier].disabledBackgroundColor && css `
            background-color: ${variationStyles[modifier].disabledBackgroundColor};
          `}
          color: ${variationStyles[modifier].disabledColor};
          opacity: 0.2;
        }

        &:focus,
        &:active,
        &:hover:enabled {
          color: ${variationStyles[modifier].hoverTextColor};
        }

        &:active,
        &:hover:enabled {
          background-color: ${(_a = variationStyles[modifier].hoverBackgroundColor) !== null && _a !== void 0 ? _a : theme.colors.gray300};
        }

        &:focus {
          ${variationStyles[modifier].boxShadow && css `
            box-shadow: ${variationStyles[modifier].boxShadow};
            `}
        }

        &:focus-visible {
          background-color: ${(_b = variationStyles[modifier].hoverBackgroundColor) !== null && _b !== void 0 ? _b : theme.colors.gray300};
          ${styleUtils.focusVisibleStyles};
        }
      `);
    }, [
        size,
        modifier,
        variationStyles,
    ]);
    return (iconButtonStyles);
};
export default useIconButtonStyles;
